interface LanguageTextEntry {
  language?: string | null
  name?: string | null
  title?: string | null
}

type LocalizedMap =
  | Record<
      string,
      | {
          name?: string | null
          title?: string | null
        }
      | undefined
    >
  | null
  | undefined

function normalizeLanguageCode(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, '-')
}

function getLanguageCandidates(language: string): string[] {
  const normalizedLanguage = language.trim()
  return normalizedLanguage ? [normalizedLanguage] : []
}

function pickText(
  value: { name?: string | null; title?: string | null } | undefined,
): string | null {
  if (!value) {
    return null
  }
  const name = (value.name ?? '').trim()
  if (name) {
    return name
  }
  const title = (value.title ?? '').trim()
  if (title) {
    return title
  }
  return null
}

function getValueFromMap(localizedData: LocalizedMap, language: string): string | null {
  if (!localizedData) {
    return null
  }

  const entries = Object.entries(localizedData).map(([key, value]) => ({
    key: normalizeLanguageCode(key),
    value,
  }))

  for (const candidate of getLanguageCandidates(language)) {
    const normalizedCandidate = normalizeLanguageCode(candidate)
    const matched = entries.find((item) => item.key === normalizedCandidate)
    const selected = pickText(matched?.value)
    if (selected) {
      return selected
    }
  }

  return null
}

function getValueFromArray(entries: LanguageTextEntry[], language: string): string | null {
  const normalizedEntries = entries.map((item) => ({
    language: normalizeLanguageCode(item.language ?? ''),
    name: (item.name ?? '').trim(),
    title: (item.title ?? '').trim(),
  }))

  for (const candidate of getLanguageCandidates(language)) {
    const normalizedCandidate = normalizeLanguageCode(candidate)
    const matched = normalizedEntries.find((item) => item.language === normalizedCandidate)
    if (matched?.name) {
      return matched.name
    }
    if (matched?.title) {
      return matched.title
    }
  }

  return null
}

function getFirstValueFromMap(localizedData: LocalizedMap): string | null {
  if (!localizedData) {
    return null
  }
  for (const value of Object.values(localizedData)) {
    const selected = pickText(value)
    if (selected) {
      return selected
    }
  }
  return null
}

function getFirstValueFromArray(entries: LanguageTextEntry[]): string | null {
  for (const entry of entries) {
    const selected = pickText(entry)
    if (selected) {
      return selected
    }
  }
  return null
}

export function resolveDashboardLocalizedValue(
  source: LocalizedMap | LanguageTextEntry[],
  activeLanguage: string,
): string {
  const primary = Array.isArray(source)
    ? getValueFromArray(source, activeLanguage)
    : getValueFromMap(source, activeLanguage)
  if (primary) {
    return primary
  }

  const fallback = Array.isArray(source)
    ? getFirstValueFromArray(source)
    : getFirstValueFromMap(source)

  return fallback ?? '-'
}
